import { useState, useRef, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type BetType = 'user' | 'counter' | 'simulated';
export type BetSide = 'left' | 'right';

export interface QueuedBet {
  id: string;
  type: BetType;
  side: BetSide;
  amount: number;
  executeAt: number; // timestamp when bet should execute
  priority: number;  // user=3, counter=2, simulated=1
}

interface ProcessedBet {
  id: string;
  side: BetSide;
  amount: number;
  isUserBet: boolean;
}

interface UseBetQueueOptions {
  onBetExecute: (bet: ProcessedBet) => void;
  isActive?: boolean;
}

const BET_PRIORITIES = {
  user: 3,
  counter: 2,
  simulated: 1,
} as const;

export const useBetQueue = ({ onBetExecute, isActive = true }: UseBetQueueOptions) => {
  const [betQueue, setBetQueue] = useState<QueuedBet[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const lastProcessTimeRef = useRef<number>(Date.now());

  // Add a bet to the queue
  const queueBet = useCallback((
    type: BetType,
    side: BetSide,
    amount: number,
    delay: number = 0
  ) => {
    const newBet: QueuedBet = {
      id: uuidv4(),
      type,
      side,
      amount,
      executeAt: Date.now() + delay,
      priority: BET_PRIORITIES[type],
    };

    setBetQueue(prev => [...prev, newBet]);
    return newBet.id;
  }, []);

  // Clear the entire queue
  const clearQueue = useCallback(() => {
    setBetQueue([]);
  }, []);

  // Remove specific bet from queue
  const removeBet = useCallback((betId: string) => {
    setBetQueue(prev => prev.filter(bet => bet.id !== betId));
  }, []);

  // Process the bet queue
  const processBetQueue = useCallback(() => {
    if (!isActive) return;

    const now = Date.now();
    
    setBetQueue(prev => {
      // Find bets that are ready to execute
      const readyBets = prev.filter(bet => bet.executeAt <= now);
      
      if (readyBets.length === 0) {
        return prev;
      }

      // Sort by priority (higher first), then by executeAt (earlier first)
      readyBets.sort((a, b) => 
        b.priority - a.priority || a.executeAt - b.executeAt
      );

      // Execute ready bets
      readyBets.forEach(bet => {
        const processedBet: ProcessedBet = {
          id: bet.id,
          side: bet.side,
          amount: bet.amount,
          isUserBet: bet.type === 'user',
        };
        onBetExecute(processedBet);
      });

      // Return queue without executed bets
      return prev.filter(bet => !readyBets.includes(bet));
    });
  }, [isActive, onBetExecute]);

  // Animation frame loop
  useEffect(() => {
    if (!isActive) {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const animate = () => {
      const now = Date.now();
      
      // Process queue at ~60fps
      if (now - lastProcessTimeRef.current >= 16) {
        processBetQueue();
        lastProcessTimeRef.current = now;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isActive, processBetQueue]);

  // Get queue stats for debugging
  const getQueueStats = useCallback(() => {
    const now = Date.now();
    return {
      total: betQueue.length,
      byType: {
        user: betQueue.filter(b => b.type === 'user').length,
        counter: betQueue.filter(b => b.type === 'counter').length,
        simulated: betQueue.filter(b => b.type === 'simulated').length,
      },
      pending: betQueue.filter(b => b.executeAt > now).length,
      ready: betQueue.filter(b => b.executeAt <= now).length,
    };
  }, [betQueue]);

  return {
    queueBet,
    clearQueue,
    removeBet,
    getQueueStats,
    betQueue,
  };
};